import React, { useEffect, useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Badge, Input, Select, Field, Modal, Spinner, Textarea } from '../ui.jsx';
import { AiControlsCard } from '../widgets/AiControlsCard.jsx';

const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const DEFAULT_AVAIL = { Mon:{on:true,start:'08:00',end:'17:00'}, Tue:{on:true,start:'08:00',end:'17:00'}, Wed:{on:true,start:'08:00',end:'17:00'}, Thu:{on:true,start:'08:00',end:'17:00'}, Fri:{on:true,start:'08:00',end:'17:00'}, Sat:{on:false,start:'09:00',end:'14:00'}, Sun:{on:false,start:'09:00',end:'14:00'} };

export function Settings({ user, toast }) {
  const [tab, setTab] = useState('profile');
  const isOwner = user?.role === 'owner' || user?.role === 'super_admin';

  const tabs = [
    { k:'profile',      label:'👤 Profile' },
    { k:'availability', label:'📅 Availability' },
    // Calendar moved to Integrations tab — it's an integration, not a personal setting
    { k:'voice',        label:'🎙️ AI Voice' },
    ...(isOwner ? [{ k:'ai-controls', label:'🎛 AI Controls' }] : []),
    { k:'security',     label:'🔒 Security' },
    ...(isOwner ? [{ k:'business', label:'🏢 Business' }, { k:'on-call', label:'🚨 On-Call Rotation' }, { k:'refer', label:'🤝 Refer & Earn' }] : []),
  ];

  return <div style={{ padding:24, maxWidth:900 }}>
    <h1 style={{ margin:'0 0 14px 0', fontSize:24, fontWeight:800 }}>Settings</h1>

    <div style={{ display:'flex', gap:4, marginBottom:18, borderBottom:`1px solid ${G.cardBorder}`, overflowX:'auto' }}>
      {tabs.map(t => (
        <button key={t.k} onClick={()=>setTab(t.k)}
          style={{ background:'none', border:'none', color: tab===t.k?G.purple:G.textMuted,
            padding:'10px 16px', fontWeight: tab===t.k?700:500, cursor:'pointer',
            borderBottom: tab===t.k?`2px solid ${G.purple}`:'2px solid transparent', marginBottom:-1, whiteSpace:'nowrap' }}>
          {t.label}
        </button>
      ))}
    </div>

    {tab === 'profile'      && <><ProfileTab user={user} toast={toast}/><DailyDigestPanel toast={toast}/><ProfitCoachPanel toast={toast}/><OwnerAssistantPanel user={user} toast={toast}/></>}
    {tab === 'availability' && <AvailabilityTab toast={toast}/>}
    {/* calendar tab removed — moved to Integrations */}
    {tab === 'voice'        && <VoiceTab toast={toast}/>}
    {tab === 'ai-controls'  && isOwner && <AiControlsCard toast={toast}/>}
    {tab === 'security'     && <SecurityTab toast={toast}/>}
    {tab === 'business' && isOwner && <BusinessTab toast={toast}/>}
    {tab === 'on-call'  && isOwner && <OnCallRotationTab toast={toast}/>}
    {tab === 'refer'    && isOwner && <ReferAndEarnTab toast={toast}/>}
  </div>;
}

function StatTile({ label, value }) {
  return <div style={{ background:G.input, padding:10, borderRadius:8 }}>
    <div style={{ fontSize:11, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em' }}>{label}</div>
    <div style={{ fontSize:18, fontWeight:800, marginTop:2 }}>{value}</div>
  </div>;
}

// ─── On-Call Rotation — who gets paged when a customer sends EMERGENCY ────
function OnCallRotationTab({ toast = () => {} }) {
  const [reps, setReps] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', email: '' });
  const [busy, setBusy] = useState(false);
  const load = () => api('/api/onboarding/on-call').then(r => setReps(r.reps || [])).catch(()=>setReps([]));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!form.name.trim() || !form.phone.trim()) return toast('Name + phone required', 'warn');
    setBusy(true);
    try { await api('/api/onboarding/on-call', { method:'POST', body: form }); setForm({ name:'', phone:'', email:'' }); load(); toast('✓ Added to rotation'); }
    catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  };
  const remove = async (id, name) => {
    if (!confirm(`Remove ${name} from on-call rotation?`)) return;
    try { await api(`/api/onboarding/on-call/${id}`, { method:'DELETE' }); load(); toast('Removed'); }
    catch (e) { toast(e.message, 'error'); }
  };
  const togglePause = async (rep) => {
    try { await api(`/api/onboarding/on-call/${rep.id}`, { method:'PATCH', body:{ active: !rep.active }}); load(); }
    catch (e) { toast(e.message, 'error'); }
  };
  return <div>
    <div style={{ ...styles.card, padding:18, marginBottom:14, borderColor:G.red+'66' }}>
      <h3 style={{ margin:'0 0 6px', fontSize:16 }}>🚨 On-Call Rotation</h3>
      <p style={{ margin:'0 0 14px', color:G.textMuted, fontSize:13 }}>
        When a customer texts "EMERGENCY" (or any of: urgent / 911 / gas leak / fire / flooding / no heat / sparking / burst pipe), the next rep in this rotation gets a page-SMS with the customer's name + number + the emergency text, plus a one-tap callback link. The rotation auto-advances after each dispatch.
      </p>

      <div style={{ display:'grid', gridTemplateColumns:'1.5fr 1.5fr 1.5fr auto', gap:8, alignItems:'end', marginBottom:14 }}>
        <Field label="Name"><Input value={form.name} onChange={e=>setForm({...form, name:e.target.value})} placeholder="Jeremy Hazelwood"/></Field>
        <Field label="Phone"><Input type="tel" value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})} placeholder="(614) 866-6020"/></Field>
        <Field label="Email (optional)"><Input type="email" value={form.email} onChange={e=>setForm({...form, email:e.target.value})} placeholder="jeremy@..."/></Field>
        <Btn v="primary" onClick={add} disabled={busy}>{busy ? <Spinner sz={14}/> : '+ Add'}</Btn>
      </div>

      {reps === null ? <Spinner sz={20}/> : reps.length === 0 ? <div style={{ color:G.textMuted, fontSize:13, padding:14, textAlign:'center', background:G.input, borderRadius:8 }}>No on-call reps configured yet. Emergencies will fall back to the account's transfer-to-human number (Settings → AI Voice) if set.</div> : <div>
        {reps.map((r, i) => (
          <div key={r.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 12px', borderBottom:`1px solid ${G.cardBorder}`, fontSize:13, opacity: r.active ? 1 : 0.55 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <span style={{ fontSize:11, color:G.textMuted, fontWeight:700, width:24 }}>#{i+1}</span>
              <div>
                <div style={{ fontWeight:700 }}>{r.name} {!r.active && <Badge c={G.textMuted} bg={G.input}>paused</Badge>}</div>
                <div style={{ fontSize:11, color:G.textMuted }}>{r.phone}{r.email ? ` · ${r.email}` : ''}{r.last_dispatched_at ? ` · last paged ${new Date(r.last_dispatched_at).toLocaleString()}` : ''}</div>
              </div>
            </div>
            <div style={{ display:'flex', gap:6 }}>
              <Btn sz="xs" v="ghost" onClick={()=>togglePause(r)}>{r.active ? 'Pause' : 'Resume'}</Btn>
              <Btn sz="xs" v="danger" onClick={()=>remove(r.id, r.name)}>Remove</Btn>
            </div>
          </div>
        ))}
      </div>}
    </div>
    <div style={{ fontSize:12, color:G.textMuted, padding:12, background:G.input, borderRadius:8 }}>
      💡 <strong>How the flow works:</strong><br/>
      1. New lead lands via webhook → AI calls within 30 sec (business hours) or texts (after hours).<br/>
      2. After-hours SMS tells customer to reply EMERGENCY if it's urgent.<br/>
      3. If they reply with an emergency keyword, the NEXT rep in this rotation gets paged with the customer's number + a tappable call link.<br/>
      4. The customer gets a reply naming the rep so they know help is coming.<br/>
      5. Rotation auto-advances so the same rep isn't paged twice in a row.
    </div>
  </div>;
}

// ─── Refer & Earn — operator-to-operator referral + testimonial capture ────
function ReferAndEarnTab({ toast = () => {} }) {
  const [ref, setRef] = useState(null);
  const [story, setStory] = useState(null);
  useEffect(() => {
    api('/api/operator-referrals/me').then(setRef).catch(()=>{});
    api('/api/testimonials/me/link').then(setStory).catch(()=>{});
  }, []);
  const copy = (text, label) => {
    try { navigator.clipboard.writeText(text); toast(`✓ ${label||'Link'} copied`); }
    catch { window.prompt('Copy this:', text); }
  };
  if (!ref) return <Spinner sz={32}/>;
  const bountyDollars = (ref.bountyCents/100).toFixed(0);
  return <div>
    <div style={{ ...styles.card, padding:18, marginBottom:14, borderColor:G.green+'66' }}>
      <h3 style={{ margin:'0 0 6px', fontSize:16 }}>💰 Earn ${bountyDollars} per operator you refer</h3>
      <p style={{ margin:'0 0 14px', color:G.textMuted, fontSize:14 }}>
        Share your link with other trades operators. When they sign up and become a paying TradeOps customer, you get ${bountyDollars} credited toward your subscription automatically. No cap.
      </p>
      <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:14 }}>
        <Input value={ref.shareUrl} readOnly style={{ fontFamily:'monospace', fontSize:13 }}/>
        <Btn v="primary" onClick={()=>copy(ref.shareUrl, 'Referral link')}>Copy</Btn>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:14 }}>
        <StatTile label="Earned" value={`$${(ref.paidOutCents/100).toFixed(0)}`}/>
        <StatTile label="Converted" value={ref.convertedCount||0}/>
        <StatTile label="Pending" value={ref.pendingCount||0}/>
      </div>
      {ref.referrals?.length > 0 && <div>
        <div style={{ fontSize:12, fontWeight:700, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:6 }}>Recent signups via your link</div>
        {ref.referrals.slice(0, 10).map(r => (
          <div key={r.id} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:`1px solid ${G.cardBorder}`, fontSize:13 }}>
            <span>{r.name}</span>
            <span style={{ color: r.convertedAt ? G.green : G.textMuted }}>
              {r.convertedAt ? `✓ Paid (${new Date(r.convertedAt).toLocaleDateString()})` : `Trialing (${r.status||'pending'})`}
            </span>
          </div>
        ))}
      </div>}
    </div>

    {story && <div style={{ ...styles.card, padding:18, marginBottom:14, borderColor:G.cyan+'66' }}>
      <h3 style={{ margin:'0 0 6px', fontSize:16 }}>📹 Share your TradeOps story</h3>
      <p style={{ margin:'0 0 14px', color:G.textMuted, fontSize:14 }}>
        90 seconds of you talking about what's different now — phone-cam selfie style is perfect. We use it on our marketing site + ads + outreach. Helps every referral you send actually convert.
      </p>
      <div style={{ display:'flex', gap:8, alignItems:'center' }}>
        <Input value={story.shareUrl} readOnly style={{ fontFamily:'monospace', fontSize:13 }}/>
        <Btn v="cyan" onClick={()=>copy(story.shareUrl, 'Testimonial link')}>Copy</Btn>
        <Btn onClick={()=>window.open(story.shareUrl, '_blank')}>Open ↗</Btn>
      </div>
    </div>}
  </div>;
}

// ─── AI Voice — pick from library or clone your own ─────────────────────────
function VoiceTab({ toast }) {
  const [voices, setVoices] = useState([]);
  const [currentVoiceId, setCurrentVoiceId] = useState('');
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(null);
  const audioRef = React.useRef(null);

  const load = () => api('/api/agents/voices').then(r => {
    setVoices(r.voices || []);
    setCurrentVoiceId(r.currentVoiceId || '');
  }).catch(e => toast(e.message,'error')).finally(()=>setLoading(false));
  useEffect(() => { load(); }, []);

  const pickVoice = async (voiceId) => {
    try {
      await api('/api/agents/voice', { method:'POST', body:{ voiceId }});
      setCurrentVoiceId(voiceId);
      toast('✅ Voice updated — new calls will use this voice');
    } catch(e) { toast(e.message,'error'); }
  };

  const playPreview = (v) => {
    if (!v.preview) return toast('No preview available','error');
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setPreviewing(v.id);
    const a = new Audio(v.preview);
    audioRef.current = a;
    a.onended = () => setPreviewing(null);
    a.play().catch(()=>{ setPreviewing(null); toast('Preview failed','error'); });
  };

  if (loading) return <Spinner sz={32}/>;

  return <div>
    <VoiceTuner toast={toast}/>

    <div style={{ ...styles.card, marginBottom:14 }}>
      <h3 style={{ margin:'0 0 6px 0', fontSize:16 }}>🧬 Clone Your Own Voice</h3>
      <div style={{ color:G.textMuted, fontSize:13, marginBottom:14 }}>
        Record 30–60 seconds of yourself talking naturally — the AI will sound exactly like you on every inbound and outbound call.
      </div>
      <VoiceCloner onCloned={(id)=>{ setCurrentVoiceId(id); load(); toast('🎉 Voice cloned and set as your AI voice'); }} toast={toast}/>
    </div>

    <div style={styles.card}>
      <h3 style={{ margin:'0 0 6px 0', fontSize:16 }}>🎤 Or Pick From the Library</h3>
      <div style={{ color:G.textMuted, fontSize:13, marginBottom:14 }}>
        Click any voice to preview. Click "Use this voice" to set it as your AI agent's voice for inbound + outbound calls.
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', gap:10 }}>
        {voices.length === 0 ? (
          <div style={{ padding:20, color:G.textMuted, textAlign:'center' }}>No voices loaded. Make sure ElevenLabs is configured.</div>
        ) : voices.map(v => {
          const isCurrent = v.id === currentVoiceId;
          return <div key={v.id} style={{ ...styles.card, padding:12, borderColor: isCurrent ? G.purple+'88' : G.cardBorder }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8, marginBottom:6 }}>
              <strong style={{ fontSize:14 }}>{v.name}</strong>
              {isCurrent && <Badge c={G.purple} bg={G.purpleDim}>Current</Badge>}
            </div>
            <div style={{ fontSize:11, color:G.textMuted, marginBottom:8, textTransform:'capitalize' }}>{v.category}</div>
            <div style={{ display:'flex', gap:6 }}>
              {v.preview && <Btn sz="xs" onClick={()=>playPreview(v)}>{previewing===v.id?'▶ Playing…':'▶ Preview'}</Btn>}
              {!isCurrent && <Btn sz="xs" v="primary" onClick={()=>pickVoice(v.id)}>Use this voice</Btn>}
            </div>
          </div>;
        })}
      </div>
    </div>
  </div>;
}

function VoiceTuner({ toast }) {
  const [t, setT] = useState(null);
  const [advanced, setAdvanced] = useState(false);
  const load = () => api('/api/agents/voice-tuning').then(setT).catch(()=>{});
  useEffect(() => { load(); }, []);
  const save = async (patch) => {
    const next = { ...t, ...patch };
    setT(next);
    try {
      await api('/api/agents/voice-tuning', { method:'PATCH', body: next });
      toast('✅ Saved — applies on the next call');
    } catch(e) { toast(e.message,'error'); }
  };
  if (!t) return null;

  const speedDescriptions = {
    fast: '⚡ Fastest — replies in under 0.5s. Uses Flash model. Best for sales/qualification calls.',
    balanced: '⚖️ Balanced — slight pause for more natural feel. Best for most use cases.',
    natural: '🌿 Most natural — slower, more thoughtful pacing. Best for high-empathy conversations.',
  };
  const characterDescriptions = {
    professional: '🎩 Calm, measured, trustworthy. Best for B2B + emergency repairs.',
    friendly:     '😊 Warm, approachable, conversational. Best for residential trades.',
    energetic:    '🔥 Upbeat, enthusiastic, sales-y. Best for closing + outbound.',
    custom:       '⚙️ Hand-tune stability, similarity, and style yourself.',
  };

  return <div style={{ ...styles.card, marginBottom:14 }}>
    <h3 style={{ margin:'0 0 6px 0', fontSize:16 }}>⚡ Response Speed & Tone</h3>
    <div style={{ color:G.textMuted, fontSize:13, marginBottom:14 }}>
      Tune how fast the AI replies and how its voice sounds. Changes apply on the next call.
    </div>

    <Field label="Response speed">
      <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
        {[['fast','⚡ Fast'],['balanced','⚖️ Balanced'],['natural','🌿 Natural']].map(([k, label]) => (
          <Btn key={k} v={t.speedPreset===k?'primary':'default'} sz="sm" onClick={()=>save({ speedPreset: k })}>{label}</Btn>
        ))}
      </div>
      <div style={{ fontSize:12, color:G.textMuted, marginTop:8 }}>{speedDescriptions[t.speedPreset]}</div>
    </Field>

    <Field label="Voice character" style={{ marginTop:14 }}>
      <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
        {[['professional','🎩 Professional'],['friendly','😊 Friendly'],['energetic','🔥 Energetic'],['custom','⚙️ Custom']].map(([k, label]) => (
          <Btn key={k} v={t.character===k?'primary':'default'} sz="sm" onClick={()=>save({ character: k })}>{label}</Btn>
        ))}
      </div>
      <div style={{ fontSize:12, color:G.textMuted, marginTop:8 }}>{characterDescriptions[t.character]}</div>
    </Field>

    <div style={{ marginTop:14 }}>
      <span onClick={()=>setAdvanced(!advanced)} style={{ cursor:'pointer', color:G.purple, fontSize:13, fontWeight:600 }}>
        {advanced ? '▾' : '▸'} Advanced tuning
      </span>
    </div>

    {advanced && <div style={{ marginTop:10, padding:14, background:G.input, borderRadius:10 }}>
      <Slider label="Pause after caller speaks (ms)" min={200} max={1500} step={50}
        value={t.silenceDurationMs} onChange={v=>save({ silenceDurationMs: v })}
        hint={`${t.silenceDurationMs}ms — lower = snappier, higher = won't interrupt`}/>

      {t.character === 'custom' && <>
        <Slider label="Stability (consistency vs expression)" min={0} max={1} step={0.05}
          value={t.stability} onChange={v=>save({ stability: v })}
          hint={`${(t.stability*100).toFixed(0)}% — higher = more monotone but predictable`}/>
        <Slider label="Similarity to source voice" min={0} max={1} step={0.05}
          value={t.similarity} onChange={v=>save({ similarity: v })}
          hint={`${(t.similarity*100).toFixed(0)}% — higher = closer to original voice`}/>
        <Slider label="Style intensity" min={0} max={1} step={0.05}
          value={t.style} onChange={v=>save({ style: v })}
          hint={`${(t.style*100).toFixed(0)}% — higher = more emotional inflection, slower`}/>
      </>}
    </div>}
  </div>;
}

function Slider({ label, min, max, step, value, onChange, hint }) {
  return <div style={{ marginTop:10 }}>
    <label style={{ fontSize:12, color:G.textMuted, display:'block', marginBottom:4 }}>{label}</label>
    <input type="range" min={min} max={max} step={step} value={value}
      onChange={e=>onChange(Number(e.target.value))}
      style={{ width:'100%' }}/>
    {hint && <div style={{ fontSize:11, color:G.textDim, marginTop:2 }}>{hint}</div>}
  </div>;
}

function VoiceCloner({ onCloned, toast }) {
  const [recording, setRecording] = useState(false);
  const [recorded, setRecorded] = useState(null); // { blob, url, durationMs }
  const [name, setName] = useState('My Voice');
  const [uploading, setUploading] = useState(false);
  const mediaRef = React.useRef(null);
  const chunksRef = React.useRef([]);
  const startTimeRef = React.useRef(0);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation:true, noiseSuppression:true }});
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' });
        const url = URL.createObjectURL(blob);
        const durationMs = Date.now() - startTimeRef.current;
        setRecorded({ blob, url, durationMs });
        stream.getTracks().forEach(t => t.stop());
      };
      mediaRef.current = mr;
      mr.start();
      startTimeRef.current = Date.now();
      setRecording(true);
      setRecorded(null);
    } catch(e) {
      toast('Microphone access denied. Allow it in your browser settings and try again.', 'error');
    }
  };
  const stop = () => { try { mediaRef.current?.stop(); } catch {}; setRecording(false); };

  const upload = async () => {
    if (!recorded?.blob) return;
    if (recorded.durationMs < 10000) return toast('Record at least 10 seconds','error');
    setUploading(true);
    try {
      const buf = await recorded.blob.arrayBuffer();
      const audioBase64 = btoa(String.fromCharCode(...new Uint8Array(buf)));
      const r = await api('/api/agents/clone-voice', { method:'POST', body:{
        name, mime: recorded.blob.type || 'audio/webm', audioBase64,
      }});
      onCloned?.(r.voiceId);
      setRecorded(null);
    } catch(e) { toast(e.message,'error'); }
    finally { setUploading(false); }
  };

  return <div>
    <Field label="Voice name (private — only you see this)" style={{ maxWidth:320 }}>
      <Input value={name} onChange={e=>setName(e.target.value)} placeholder="My Voice"/>
    </Field>
    <div style={{ marginTop:12, padding:14, background:G.input, borderRadius:10 }}>
      {!recorded && !recording && <>
        <div style={{ fontSize:13, marginBottom:10 }}>
          📝 <strong>Sample script (read this clearly):</strong>
          <div style={{ marginTop:6, padding:10, background:G.bg, borderRadius:8, fontSize:13, lineHeight:1.6, color:G.textMuted }}>
            "Hey, this is [your name] over at [your business]. I just wanted to follow up on the request you sent in. I've been doing this work for over fifteen years, and I'd love to learn more about what you're looking for. When would be a good time for us to chat for a couple minutes? I can probably get you a quote on the spot."
          </div>
        </div>
        <Btn v="primary" onClick={start}>🎙️ Start recording</Btn>
      </>}

      {recording && <div style={{ display:'flex', alignItems:'center', gap:14 }}>
        <div style={{ width:14, height:14, borderRadius:99, background:G.red||'#ef4444', animation:'pulse 1s infinite' }}/>
        <span style={{ fontWeight:600 }}>Recording... read the script above</span>
        <Btn v="danger" onClick={stop}>⏹ Stop</Btn>
      </div>}

      {recorded && <div>
        <audio controls src={recorded.url} style={{ width:'100%', marginBottom:10 }}/>
        <div style={{ fontSize:12, color:G.textMuted, marginBottom:10 }}>
          Recorded {Math.round(recorded.durationMs/1000)}s · {(recorded.blob.size/1024).toFixed(0)} KB
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <Btn onClick={()=>setRecorded(null)} disabled={uploading}>🔁 Re-record</Btn>
          <Btn v="primary" onClick={upload} disabled={uploading || recorded.durationMs < 10000}>
            {uploading ? <Spinner sz={14}/> : '✨ Clone this voice'}
          </Btn>
        </div>
        {recorded.durationMs < 10000 && <div style={{ fontSize:11, color:G.yellow, marginTop:6 }}>⚠️ At least 10 seconds is required for cloning. Longer (30–60s) gives better results.</div>}
      </div>}
    </div>
  </div>;
}

function ProfileTab({ user, toast }) {
  const [f, setF] = useState({ firstName:'', lastName:'', email:'', phone:'' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    setF({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      phone: user.phone || '',
    });
  }, [user]);

  const save = async () => {
    setSaving(true);
    try {
      await api('/api/auth/profile', { method:'PATCH', body: f });
      toast('✅ Profile saved');
    } catch(e) { toast(e.message,'error'); } finally { setSaving(false); }
  };

  return <div style={styles.card}>
    <h3 style={{ margin:'0 0 14px 0', fontSize:16 }}>Your Profile</h3>
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
      <Field label="First Name"><Input value={f.firstName} onChange={e=>setF({...f,firstName:e.target.value})}/></Field>
      <Field label="Last Name"><Input value={f.lastName} onChange={e=>setF({...f,lastName:e.target.value})}/></Field>
      <Field label="Email"><Input value={f.email} onChange={e=>setF({...f,email:e.target.value})} type="email"/></Field>
      <Field label="Phone"><Input value={f.phone} onChange={e=>setF({...f,phone:e.target.value})} placeholder="(555) 123-4567"/></Field>
    </div>
    <div style={{ marginTop:14 }}>
      <Btn v="primary" onClick={save} disabled={saving}>{saving?<Spinner sz={14}/>:'Save Profile'}</Btn>
    </div>
  </div>;
}

function AvailabilityTab({ toast }) {
  const [avail, setAvail] = useState(DEFAULT_AVAIL);
  const [tz, setTz] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api('/api/auth/availability').then(r => {
      if (r?.availability) setAvail({ ...DEFAULT_AVAIL, ...r.availability });
      if (r?.timezone) setTz(r.timezone);
    }).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api('/api/auth/availability', { method:'PATCH', body: { availability: avail, timezone: tz }});
      toast('✅ Availability saved');
    } catch(e) { toast(e.message,'error'); } finally { setSaving(false); }
  };

  if (loading) return <Spinner sz={32}/>;

  return <div style={styles.card}>
    <h3 style={{ margin:'0 0 6px 0', fontSize:16 }}>Working Hours</h3>
    <div style={{ color:G.textMuted, fontSize:13, marginBottom:14 }}>
      Used by the AI to book calls within your available hours and by your team for scheduling.
    </div>

    <Field label="Timezone" style={{ maxWidth:320, marginBottom:18 }}>
      <Select value={tz} onChange={e=>setTz(e.target.value)}>
        <option value="America/New_York">Eastern (New York)</option>
        <option value="America/Chicago">Central (Chicago)</option>
        <option value="America/Denver">Mountain (Denver)</option>
        <option value="America/Phoenix">Arizona (no DST)</option>
        <option value="America/Los_Angeles">Pacific (Los Angeles)</option>
        <option value="America/Anchorage">Alaska</option>
        <option value="Pacific/Honolulu">Hawaii</option>
      </Select>
    </Field>

    <div style={{ display:'grid', gap:8 }}>
      {DAYS.map(d => {
        const row = avail[d] || { on:false, start:'09:00', end:'17:00' };
        return (
          <div key={d} style={{ display:'grid', gridTemplateColumns:'80px 60px 1fr 14px 1fr', alignItems:'center', gap:10 }}>
            <strong style={{ width:60 }}>{d}</strong>
            <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', fontSize:13 }}>
              <input type="checkbox" checked={!!row.on} onChange={e=>setAvail({...avail, [d]:{...row, on:e.target.checked}})}/>
              {row.on ? 'On' : 'Off'}
            </label>
            <Input type="time" value={row.start} disabled={!row.on} onChange={e=>setAvail({...avail, [d]:{...row, start:e.target.value}})}/>
            <span style={{ textAlign:'center', color:G.textMuted }}>→</span>
            <Input type="time" value={row.end} disabled={!row.on} onChange={e=>setAvail({...avail, [d]:{...row, end:e.target.value}})}/>
          </div>
        );
      })}
    </div>

    <div style={{ marginTop:18 }}>
      <Btn v="primary" onClick={save} disabled={saving}>{saving?<Spinner sz={14}/>:'Save Availability'}</Btn>
    </div>
  </div>;
}

function CalendarTab({ user, toast }) {
  const [conns, setConns] = useState({});
  const load = () => api('/api/integrations/connections').then(setConns).catch(()=>{});
  useEffect(() => { load(); }, []);

  const connect = async (provider) => {
    try { const { url } = await api(`/api/integrations/${provider}/connect`); window.location.href = url; }
    catch(e) { toast(e.message,'error'); }
  };
  const disconnect = async (provider) => {
    if (!confirm(`Disconnect ${provider==='gcal'?'Google Calendar':'Outlook'}?`)) return;
    try { await api(`/api/integrations/${provider}/disconnect`, { method:'POST' }); load(); toast('Disconnected'); }
    catch(e) { toast(e.message,'error'); }
  };

  const cal = (provider, label, icon, color) => {
    const c = conns[provider] || {};
    return <div key={provider} style={{ ...styles.card, borderColor: c.connected ? color+'66' : G.cardBorder, marginBottom:12 }}>
      <div style={{ display:'flex', alignItems:'center', gap:12 }}>
        <div style={{ width:42, height:42, borderRadius:10, background:color+'22', color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:22 }}>{icon}</div>
        <div style={{ flex:1 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <strong style={{ fontSize:15 }}>{label}</strong>
            {c.connected
              ? <Badge c={G.green} bg={G.greenLight}>Connected</Badge>
              : <Badge c={G.textMuted} bg={G.input}>Not connected</Badge>}
          </div>
          <div style={{ fontSize:12, color:G.textMuted, marginTop:2 }}>
            {c.connected ? c.identity : 'Auto-blocks busy times. Jobs sync both ways.'}
          </div>
        </div>
        {c.connected
          ? <Btn sz="sm" v="danger" onClick={()=>disconnect(provider)}>Disconnect</Btn>
          : <Btn sz="sm" v="primary" onClick={()=>connect(provider)}>Connect</Btn>}
      </div>
    </div>;
  };

  return <div>
    <div style={{ marginBottom:14, color:G.textMuted, fontSize:13 }}>
      Two-way calendar sync. Booked jobs appear in your calendar; meetings on your calendar block your booking availability.
    </div>
    {cal('gcal',    'Google Calendar', '🗓️', '#4285f4')}
    {cal('outlook', 'Outlook Calendar', '📆', '#0078d4')}
  </div>;
}

function SecurityTab({ toast }) {
  const [f, setF] = useState({ current:'', next:'', confirm:'' });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (f.next !== f.confirm) return toast('New passwords do not match','error');
    if (f.next.length < 8) return toast('Password must be at least 8 characters','error');
    setSaving(true);
    try {
      await api('/api/auth/change-password', { method:'POST', body:{ currentPassword: f.current, newPassword: f.next }});
      toast('✅ Password updated');
      setF({ current:'', next:'', confirm:'' });
    } catch(e) { toast(e.message,'error'); } finally { setSaving(false); }
  };
  return <div>
    <TwoFactorPanel toast={toast}/>
    <div style={styles.card}>
      <h3 style={{ margin:'0 0 14px 0', fontSize:16 }}>Change Password</h3>
      <div style={{ display:'grid', gap:12, maxWidth:380 }}>
        <Field label="Current Password"><Input type="password" value={f.current} onChange={e=>setF({...f,current:e.target.value})} autoComplete="current-password"/></Field>
        <Field label="New Password"><Input type="password" value={f.next} onChange={e=>setF({...f,next:e.target.value})} autoComplete="new-password"/></Field>
        <Field label="Confirm New Password"><Input type="password" value={f.confirm} onChange={e=>setF({...f,confirm:e.target.value})} autoComplete="new-password"/></Field>
      </div>
      <div style={{ marginTop:14 }}>
        <Btn v="primary" onClick={save} disabled={saving || !f.current || !f.next}>{saving?<Spinner sz={14}/>:'Update Password'}</Btn>
      </div>
    </div>
  </div>;
}

function TwoFactorPanel({ toast }) {
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState('');
  const [pw, setPw] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);
  const load = () => api('/api/auth/2fa/status').then(setStatus).catch(()=>{});
  useEffect(() => { load(); }, []);
  const startSetup = async () => {
    try { setSetup(await api('/api/auth/2fa/setup', { method:'POST' })); }
    catch(e) { toast(e.message,'error'); }
  };
  const verify = async () => {
    if (!code) return toast('Enter the 6-digit code','error');
    try {
      const r = await api('/api/auth/2fa/verify', { method:'POST', body:{ token: code }});
      setBackupCodes(r.backupCodes);
      setSetup(null); setCode('');
      await load();
      toast('🔒 2FA enabled');
    } catch(e) { toast(e.message,'error'); }
  };
  const disable = async () => {
    if (!confirm('Disable 2FA? Your account will be less secure.')) return;
    if (!pw) return toast('Enter your password to disable','error');
    try {
      await api('/api/auth/2fa/disable', { method:'POST', body:{ password: pw }});
      setPw('');
      await load();
      toast('2FA disabled');
    } catch(e) { toast(e.message,'error'); }
  };
  if (!status) return null;
  return <div style={{ ...styles.card, marginBottom:14 }}>
    <h3 style={{ margin:'0 0 6px 0', fontSize:16 }}>🔐 Two-Factor Authentication</h3>
    <div style={{ color:G.textMuted, fontSize:13, marginBottom:14 }}>
      Requires a 6-digit code from your authenticator app (Google Authenticator, Authy, 1Password, Bitwarden) at login. Even if your password leaks, your account stays protected.
    </div>
    {status.enabled ? (
      <div>
        <Badge c={G.green} bg={G.greenLight}>✓ Enabled</Badge>
        <div style={{ display:'flex', gap:8, marginTop:14, alignItems:'center' }}>
          <Input type="password" placeholder="Password to disable" value={pw} onChange={e=>setPw(e.target.value)} style={{ maxWidth:240 }}/>
          <Btn v="danger" onClick={disable} disabled={!pw}>Disable 2FA</Btn>
        </div>
      </div>
    ) : !setup ? (
      <Btn v="primary" onClick={startSetup}>Enable 2FA</Btn>
    ) : (
      <div>
        <div style={{ marginBottom:10, fontSize:13 }}>1. Scan this QR with your authenticator:</div>
        <img src={setup.qrCodeDataUrl} alt="2FA QR" style={{ width:200, background:'#fff', padding:8, borderRadius:8 }}/>
        <div style={{ marginTop:10, fontSize:12, color:G.textMuted }}>Or enter this code manually: <code style={{ background:G.input, padding:'2px 6px', borderRadius:4 }}>{setup.secret}</code></div>
        <div style={{ marginTop:14, display:'flex', gap:8, alignItems:'center' }}>
          <Input placeholder="6-digit code" value={code} onChange={e=>setCode(e.target.value.replace(/\D/g,''))} maxLength={6} style={{ maxWidth:160 }}/>
          <Btn v="primary" onClick={verify} disabled={code.length !== 6}>Verify + Enable</Btn>
        </div>
      </div>
    )}
    {backupCodes && <div style={{ marginTop:14, padding:14, background:G.input, borderRadius:8 }}>
      <strong>⚠️ Save these backup codes</strong>
      <div style={{ fontSize:12, color:G.textMuted, marginBottom:8 }}>If you lose your phone, use one of these to log in. Each one works once.</div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(2, 1fr)', gap:8, fontFamily:'monospace', fontSize:14 }}>
        {backupCodes.map(c => <div key={c} style={{ padding:'6px 10px', background:G.bg, borderRadius:4 }}>{c}</div>)}
      </div>
    </div>}
  </div>;
}

function DailyDigestPanel({ toast }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api('/api/admin-panel/digest/settings').then(setS).catch(()=>{});
  useEffect(() => { load(); }, []);
  const save = async (patch) => {
    const next = { ...s, ...patch };
    setS(next);
    try { await api('/api/admin-panel/digest/settings', { method:'PATCH', body: next }); toast('Saved'); }
    catch(e) { toast(e.message,'error'); }
  };
  const sendTest = async () => {
    setBusy(true);
    try {
      const r = await api('/api/admin-panel/digest/test', { method:'POST' });
      if (r.ok) toast('✅ Test digest sent — check your phone');
      else toast(`Skipped: ${r.reason || r.error || 'unknown'}`, 'error');
      load();
    } catch(e) { toast(e.message,'error'); } finally { setBusy(false); }
  };
  if (!s) return null;
  return <div style={{ ...styles.card, marginTop:14 }}>
    <h3 style={{ margin:'0 0 6px 0', fontSize:16 }}>📊 Daily Digest SMS</h3>
    <div style={{ color:G.textMuted, fontSize:13, marginBottom:14 }}>
      Each morning we text you a 4-line summary: yesterday's wins, today's plan, what needs your attention, and the biggest opportunity. Reply with anything — your texts run as commands (e.g. "send Sarah a reminder about Tuesday").
    </div>
    <div style={{ display:'flex', gap:14, alignItems:'center', flexWrap:'wrap' }}>
      <label style={{ display:'flex', alignItems:'center', gap:8 }}>
        <input type="checkbox" checked={s.enabled} onChange={e=>save({ enabled: e.target.checked })}/>
        <span style={{ fontSize:14 }}>Enabled</span>
      </label>
      <Field label="Send at" style={{ marginBottom:0 }}>
        <Select value={s.hour} onChange={e=>save({ hour: Number(e.target.value) })} style={{ width:120 }}>
          {Array.from({length:24}, (_,i)=>i).map(h => <option key={h} value={h}>{h}:00</option>)}
        </Select>
      </Field>
      <Btn sz="sm" v="primary" onClick={sendTest} disabled={busy || !s.enabled}>{busy?<Spinner sz={14}/>:'📲 Send test now'}</Btn>
    </div>
    {s.lastSent && <div style={{ fontSize:12, color:G.textMuted, marginTop:10 }}>Last sent: {new Date(s.lastSent).toLocaleDateString()}</div>}
  </div>;
}

function ProfitCoachPanel({ toast }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api('/api/admin-panel/profit-coach/settings').then(setS).catch(()=>{});
  useEffect(() => { load(); }, []);
  const save = async (patch) => {
    const next = { ...s, ...patch };
    setS(next);
    try { await api('/api/admin-panel/profit-coach/settings', { method:'PATCH', body: next }); toast('Saved'); }
    catch(e) { toast(e.message,'error'); }
  };
  const sendNow = async (cadence) => {
    setBusy(true);
    try {
      const r = await api('/api/financials/profit-coach/send', { method:'POST', body:{ cadence }});
      toast(`✓ ${cadence === 'weekly' ? 'Weekly' : 'Monthly'} report sent to ${r.email} — ${r.leakCount} leak${r.leakCount===1?'':'s'} · ~$${Math.round(r.totalPotentialSavings||0).toLocaleString()}/yr recoverable`);
      load();
    } catch(e) { toast(e.message,'error'); } finally { setBusy(false); }
  };
  if (!s) return null;
  return <div style={{ ...styles.card, marginTop:14 }}>
    <h3 style={{ margin:'0 0 6px 0', fontSize:16 }}>📈 Profit Coach Email</h3>
    <div style={{ color:G.textMuted, fontSize:13, marginBottom:14, lineHeight:1.5 }}>
      A coaching report sent to <strong style={{ color:G.text }}>{s.recipient || 'your account email'}</strong> that analyzes your P&L for revenue leaks — subcontractor cost overruns, advertising waste, pricing gaps, software bloat — and gives you specific actions ranked by dollar impact. Matter-of-fact, no fluff. Weekly = last 7 days, monthly = last 30 days.
    </div>
    <div style={{ display:'flex', gap:18, alignItems:'center', flexWrap:'wrap', marginBottom:12 }}>
      <label style={{ display:'flex', alignItems:'center', gap:8 }}>
        <input type="checkbox" checked={s.weekly} onChange={e=>save({ weekly: e.target.checked })}/>
        <span style={{ fontSize:14 }}>Weekly (Monday 7am)</span>
      </label>
      <label style={{ display:'flex', alignItems:'center', gap:8 }}>
        <input type="checkbox" checked={s.monthly} onChange={e=>save({ monthly: e.target.checked })}/>
        <span style={{ fontSize:14 }}>Monthly (1st of month 7am)</span>
      </label>
    </div>
    <div style={{ background:G.input, padding:'10px 12px', borderRadius:8, marginBottom:12 }}>
      <label style={{ display:'flex', alignItems:'center', gap:8 }}>
        <input type="checkbox" checked={!!s.autoSend} onChange={e=>save({ autoSend: e.target.checked })}/>
        <div>
          <div style={{ fontSize:13, fontWeight:600 }}>Auto-send (skip draft review)</div>
          <div style={{ fontSize:11, color:G.textMuted, marginTop:2 }}>{s.autoSend ? 'Reports email directly on schedule.' : 'Reports save as drafts for you to review + send manually (recommended while we calibrate the analysis).'}</div>
        </div>
      </label>
    </div>
    <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
      <Btn sz="sm" v="primary" onClick={()=>sendNow('weekly')} disabled={busy}>{busy?<Spinner sz={14}/>:'📧 Send weekly preview now'}</Btn>
      <Btn sz="sm" v="purple" onClick={()=>sendNow('monthly')} disabled={busy}>{busy?<Spinner sz={14}/>:'📧 Send monthly preview now'}</Btn>
    </div>
    {(s.lastWeekly || s.lastMonthly) && <div style={{ fontSize:12, color:G.textMuted, marginTop:10 }}>
      {s.lastWeekly && <>Last weekly: {new Date(s.lastWeekly).toLocaleDateString()}</>}
      {s.lastWeekly && s.lastMonthly && ' · '}
      {s.lastMonthly && <>Last monthly: {new Date(s.lastMonthly).toLocaleDateString()}</>}
    </div>}
  </div>;
}

function OwnerAssistantPanel({ user, toast }) {
  const phone = user?.phone || '';
  return <div style={{ ...styles.card, marginTop:14 }}>
    <h3 style={{ margin:'0 0 6px 0', fontSize:16 }}>📲 SMS + Voice Owner Assistant</h3>
    <div style={{ color:G.textMuted, fontSize:13, marginBottom:14 }}>
      Text or call your business number from <strong>{phone || 'your phone'}</strong> and Claude executes the command. Customers still get the AI auto-reply — only messages from your verified phone get the assistant.
    </div>
    <div style={{ padding:14, background:G.input, borderRadius:10 }}>
      <strong>Try texting your business number:</strong>
      <ul style={{ marginTop:8, paddingLeft:20, lineHeight:1.8, fontSize:13 }}>
        <li><em>"What's on the schedule today?"</em></li>
        <li><em>"Send Sarah Thompson a reminder about Tuesday at 2"</em></li>
        <li><em>"Show me overdue invoices over $500"</em></li>
        <li><em>"Schedule Mike for the Smith install Thursday at 10am"</em></li>
        <li><em>"Have the AI call every overdue invoice"</em></li>
        <li><em>"Add a note to John Doe — gate code is 4271"</em></li>
      </ul>
      <div style={{ marginTop:10, padding:10, background:G.bg, borderRadius:8, fontSize:12, color:G.text, lineHeight:1.6 }}>
        🎙️ <strong>Voice notes via SMS:</strong> open your business number conversation in your phone's Messages app → hold the mic button → speak → release to send. We transcribe via Whisper → execute → text you the result. No calling in.
        <br/><br/>
        <em>Hands-free in the truck:</em> "Hey Siri, send a voice message to my business" → speak the command → Siri sends. Done.
      </div>
    </div>
    {!phone && <div style={{ marginTop:10, padding:10, background:G.yellowLight, borderRadius:8, fontSize:12 }}>
      ⚠️ Add your phone number above so the assistant can identify you. Without it, your texts get treated like a normal customer.
    </div>}
  </div>;
}

function BusinessTab({ toast }) {
  const [a, setA] = useState({ name:'', industry:'', phone:'', email:'', address:'', city:'', state:'', zip:'', website:'', timezone:'America/New_York' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api('/api/auth/account-info').then(r => { setA({ ...a, ...r }); }).catch(()=>{}).finally(()=>setLoading(false));
    // eslint-disable-next-line
  }, []);
  const save = async () => {
    setSaving(true);
    try { await api('/api/auth/account-info', { method:'PATCH', body: a }); toast('✅ Business info saved'); }
    catch(e) { toast(e.message,'error'); } finally { setSaving(false); }
  };
  if (loading) return <Spinner sz={32}/>;
  return <div style={styles.card}>
    <h3 style={{ margin:'0 0 14px 0', fontSize:16 }}>Business Info</h3>
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
      <Field label="Business Name" style={{ gridColumn:'1/-1' }}><Input value={a.name||''} onChange={e=>setA({...a,name:e.target.value})}/></Field>
      <Field label="Industry">
        <Select value={a.industry||''} onChange={e=>setA({...a,industry:e.target.value})}>
          <option value="">—</option>
          <option>HVAC</option><option>Plumbing</option><option>Electrical</option>
          <option>Roofing</option><option>Landscaping</option><option>General Contracting</option><option>Other</option>
        </Select>
      </Field>
      <Field label="Phone"><Input value={a.phone||''} onChange={e=>setA({...a,phone:e.target.value})}/></Field>
      <Field label="Email"><Input value={a.email||''} onChange={e=>setA({...a,email:e.target.value})} type="email"/></Field>
      <Field label="Website"><Input value={a.website||''} onChange={e=>setA({...a,website:e.target.value})} placeholder="https://"/></Field>
      <Field label="Address" style={{ gridColumn:'1/-1' }}><Input value={a.address||''} onChange={e=>setA({...a,address:e.target.value})}/></Field>
      <Field label="City"><Input value={a.city||''} onChange={e=>setA({...a,city:e.target.value})}/></Field>
      <Field label="State"><Input value={a.state||''} onChange={e=>setA({...a,state:e.target.value})} placeholder="FL"/></Field>
      <Field label="ZIP"><Input value={a.zip||''} onChange={e=>setA({...a,zip:e.target.value})}/></Field>
      <Field label="Timezone">
        <Select value={a.timezone||'America/New_York'} onChange={e=>setA({...a,timezone:e.target.value})}>
          <option value="America/New_York">Eastern</option>
          <option value="America/Chicago">Central</option>
          <option value="America/Denver">Mountain</option>
          <option value="America/Los_Angeles">Pacific</option>
        </Select>
      </Field>
    </div>
    <div style={{ marginTop:14 }}>
      <Btn v="primary" onClick={save} disabled={saving}>{saving?<Spinner sz={14}/>:'Save Business Info'}</Btn>
    </div>
    <TipCollectionPanel toast={toast}/>
  </div>;
}

// Tip collection toggle. Hidden behind a small disclosure since most owners
// won't want to use it on residential service work — but the wiring is in
// Stripe Checkout so it works the moment the flag flips on.
function TipCollectionPanel({ toast }) {
  const [enabled, setEnabled] = useState(false);
  const [percents, setPercents] = useState('10,15,20');
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    api('/api/auth/account-info').then(r => {
      setEnabled(!!r.tip_collection_enabled);
      try { const p = JSON.parse(r.tip_suggested_percents || '[10,15,20]'); setPercents(p.join(',')); } catch {}
    }).catch(()=>{});
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      const parsed = String(percents).split(',').map(s => Number(s.trim())).filter(n => !isNaN(n) && n > 0 && n < 100);
      await api('/api/auth/account-info', { method:'PATCH', body:{ tip_collection_enabled: enabled ? 1 : 0, tip_suggested_percents: JSON.stringify(parsed.length ? parsed : [10,15,20]) }});
      toast('✅ Tip settings saved');
    } catch (e) { toast(e.message, 'error'); }
    finally { setSaving(false); }
  };
  return <div style={{ ...styles.card, marginTop:14, padding:16 }}>
    <div onClick={()=>setOpen(!open)} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', cursor:'pointer' }}>
      <h3 style={{ margin:0, fontSize:14 }}>💵 Tip Collection {enabled && <Badge c={G.green} bg={G.greenLight}>ON</Badge>}</h3>
      <span style={{ fontSize:12, color:G.textMuted }}>{open ? '▴ Hide' : '▾ Show'}</span>
    </div>
    {open && <div style={{ marginTop:12 }}>
      <div style={{ fontSize:12, color:G.textMuted, marginBottom:10, lineHeight:1.55 }}>
        When enabled, Stripe Checkout will prompt the customer to add an optional tip before paying. Most residential trades businesses leave this OFF; turn on if you want to give customers the option.
      </div>
      <label style={{ display:'flex', gap:10, alignItems:'center', cursor:'pointer', marginBottom:14 }}>
        <input type="checkbox" checked={enabled} onChange={e=>setEnabled(e.target.checked)} style={{ width:18, height:18, cursor:'pointer' }}/>
        <span style={{ fontSize:13 }}>Enable tip collection on invoices</span>
      </label>
      <Field label="Suggested percent options (comma-separated)" hint="e.g. 10,15,20">
        <Input value={percents} onChange={e=>setPercents(e.target.value)} placeholder="10,15,20" disabled={!enabled}/>
      </Field>
      <Btn v="primary" sz="sm" onClick={save} disabled={saving}>{saving?<Spinner sz={12}/>:'Save tip settings'}</Btn>
    </div>}
  </div>;
}
